import { EmotionalArc } from '@/types';

export const EMOTIONAL_ARCS: EmotionalArc[] = [
  {
    id: 'slow-burn',
    label: 'Slow Burn',
    description: 'Starts simmering and builds relentlessly to a boiling climax. The longer the wait, the bigger the payoff.',
    points: [
      { position: 0, emotion: 'Restrained', intensity: 20 },
      { position: 25, emotion: 'Warming', intensity: 35 },
      { position: 50, emotion: 'Building', intensity: 55 },
      { position: 75, emotion: 'Surging', intensity: 75 },
      { position: 100, emotion: 'Erupting', intensity: 90 }
    ],
    physicsPrompt: `{ARC-PHYSICS: SLOW-BURN}
[Arc_Behavior: Progressive intensity escalation; Each section adds 15-20% vocal energy; Never retreat]
[Arc_Dynamics: Start at 20% projection; Build through mid-range by verse 2; Full power by final chorus]
[Arc_Character: Patient build; Earned climax; Relentless forward momentum]`,
    sunoTags: ['building intensity', 'crescendo', 'slow build', 'climactic']
  },
  {
    id: 'roller-coaster',
    label: 'Roller Coaster',
    description: 'Oscillating intensity that keeps the listener off-balance. Peaks and valleys create constant emotional motion.',
    points: [
      { position: 0, emotion: 'Charged', intensity: 50 },
      { position: 20, emotion: 'Exploding', intensity: 85 },
      { position: 35, emotion: 'Retreating', intensity: 30 },
      { position: 55, emotion: 'Surging', intensity: 80 },
      { position: 70, emotion: 'Dropping', intensity: 25 },
      { position: 85, emotion: 'Final Push', intensity: 90 },
      { position: 100, emotion: 'Landing', intensity: 60 }
    ],
    physicsPrompt: `{ARC-PHYSICS: ROLLER-COASTER}
[Arc_Behavior: Oscillating intensity waves; Sharp transitions between peaks and valleys; Unpredictable dynamics]
[Arc_Dynamics: Alternate between 25-30% and 80-90% energy; Rapid transition speed; No settling]
[Arc_Character: Emotional whiplash; Constant surprise; Dynamic instability as feature]`,
    sunoTags: ['dynamic vocals', 'emotional range', 'unpredictable', 'contrasting dynamics']
  },
  {
    id: 'descent-into-darkness',
    label: 'Descent into Darkness',
    description: 'Starts bright and confident, then spirals down into heavy, dark, defeated territory. The light fades.',
    points: [
      { position: 0, emotion: 'Confident', intensity: 80 },
      { position: 25, emotion: 'Wavering', intensity: 65 },
      { position: 50, emotion: 'Cracking', intensity: 45 },
      { position: 75, emotion: 'Breaking', intensity: 30 },
      { position: 100, emotion: 'Defeated', intensity: 20 }
    ],
    physicsPrompt: `{ARC-PHYSICS: DESCENT-INTO-DARKNESS}
[Arc_Behavior: Progressive intensity reduction; Vocal confidence eroding section by section; Darkening timbre]
[Arc_Dynamics: Start at 80% power; Lose 15% per section; End at whisper-threshold]
[Arc_Character: Hope fading; Strength draining; Final delivery is barely held together]`,
    sunoTags: ['dark vocals', 'fading power', 'descending', 'melancholy', 'bleak']
  },
  {
    id: 'explosion',
    label: 'Explosion',
    description: 'Quiet tension snaps into sudden maximum power, then settles into confident afterglow. The detonation moment.',
    points: [
      { position: 0, emotion: 'Tense Whisper', intensity: 20 },
      { position: 30, emotion: 'Building Pressure', intensity: 35 },
      { position: 45, emotion: 'DETONATION', intensity: 100 },
      { position: 60, emotion: 'Shockwave', intensity: 85 },
      { position: 80, emotion: 'Settling', intensity: 65 },
      { position: 100, emotion: 'Afterglow', intensity: 55 }
    ],
    physicsPrompt: `{ARC-PHYSICS: EXPLOSION}
[Arc_Behavior: Extended quiet tension; Single massive detonation point; Post-blast sustained energy]
[Arc_Dynamics: 20% whisper build; Instant jump to 100% at detonation; Settle to 55-65% afterglow]
[Arc_Character: Maximum contrast; Single defining moment; Everything before and after serves the blast]`,
    sunoTags: ['explosive vocals', 'dynamic contrast', 'powerful climax', 'tension release']
  },
  {
    id: 'steady-burn',
    label: 'Steady Burn',
    description: 'Consistent high-energy delivery throughout. No peaks, no valleys — just relentless sustained intensity.',
    points: [
      { position: 0, emotion: 'Locked In', intensity: 70 },
      { position: 25, emotion: 'Maintaining', intensity: 72 },
      { position: 50, emotion: 'Unwavering', intensity: 70 },
      { position: 75, emotion: 'Relentless', intensity: 73 },
      { position: 100, emotion: 'Still Burning', intensity: 70 }
    ],
    physicsPrompt: `{ARC-PHYSICS: STEADY-BURN}
[Arc_Behavior: Flat-line high intensity; No dynamic variation; Relentless sustained delivery]
[Arc_Dynamics: Lock at 70% power; Maintain through all sections; Endurance over dynamics]
[Arc_Character: Punk-rock commitment; Marathon runner mentality; Consistency is the weapon]`,
    sunoTags: ['consistent energy', 'relentless', 'sustained power', 'driving vocals']
  },
  {
    id: 'resurrection',
    label: 'Resurrection',
    description: 'Starts strong, falls to nothing, then rises from the ashes even stronger. Death and rebirth in one song.',
    points: [
      { position: 0, emotion: 'Standing Tall', intensity: 75 },
      { position: 20, emotion: 'Faltering', intensity: 55 },
      { position: 40, emotion: 'Falling', intensity: 30 },
      { position: 55, emotion: 'Rock Bottom', intensity: 15 },
      { position: 70, emotion: 'First Breath', intensity: 40 },
      { position: 85, emotion: 'Rising', intensity: 70 },
      { position: 100, emotion: 'Reborn', intensity: 95 }
    ],
    physicsPrompt: `{ARC-PHYSICS: RESURRECTION}
[Arc_Behavior: Strong start; Progressive collapse to near-silence; Triumphant rebuild exceeding original]
[Arc_Dynamics: Open at 75%; Crash to 15% at midpoint; Rebuild to 95% by final section]
[Arc_Character: Fall-and-rise narrative; Rock-bottom vulnerability; Phoenix-from-ashes triumph]`,
    sunoTags: ['triumphant', 'resurrection arc', 'rise and fall', 'comeback', 'emotional journey']
  }
];

export function getEmotionalArcById(id: string): EmotionalArc | undefined {
  return EMOTIONAL_ARCS.find(a => a.id === id);
}
